/* eslint-disable react/prop-types */
/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Spacer,
  Stack,
  Tag,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiClock,
  FiRepeat,
  FiArrowRight,
  FiFile,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import moment from 'moment';

import {
  deleteDocumentFirebase,
  getSingleDocumentFirebase,
  updateDocumentFirebase,
} from '../../Api/firebaseApi';
import BackButtons from '../../Components/Buttons/BackButtons';
import DynamicButton from '../../Components/Buttons/DynamicButton';
import AlertDialogV2 from '../../Components/AlertDialog/AlertDialogV2';

// ==================== INFO CARD COMPONENT ====================
const InfoCard = ({ icon, label, value, colorScheme = 'gray' }) => (
  <HStack
    spacing={3}
    p={4}
    bg="white"
    borderRadius="lg"
    border="1px"
    borderColor="gray.200"
    _hover={{ shadow: 'md', borderColor: `${colorScheme}.300` }}
    transition="all 0.2s"
  >
    <Icon as={icon} boxSize={5} color={`${colorScheme}.500`} />
    <VStack align="flex-start" spacing={0} flex={1}>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        {value || '-'}
      </Text>
    </VStack>
  </HStack>
);

// ==================== SECTION CARD COMPONENT ====================
const SectionCard = ({ title, icon, children, colorScheme = 'blue' }) => (
  <Stack
    bg="white"
    p={6}
    borderRadius="xl"
    shadow="sm"
    border="1px"
    borderColor="gray.200"
    spacing={4}
  >
    <HStack spacing={3}>
      <Icon as={icon} boxSize={6} color={`${colorScheme}.500`} />
      <Heading size="md" color="gray.700">
        {title}
      </Heading>
    </HStack>
    <Divider />
    {children}
  </Stack>
);

// ==================== MAIN COMPONENT ====================
function AutomationDetailPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const alertDelete = useDisclosure();

  const [data, setData] = useState(state || null);
  const [isLoading, setIsLoading] = useState(!state);

  // ==================== FETCH DATA ====================
  const getData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await getSingleDocumentFirebase('automation', id);
      setData(result);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    getData();
  }, [getData]);

  // ==================== DELETE HANDLER ====================
  const handleDelete = useCallback(async () => {
    if (!data) return;

    try {
      await deleteDocumentFirebase('automation', data.id);

      // Update source document if repeat action
      if (data.action === 'repeat' && data.type && data.typeId) {
        await updateDocumentFirebase(data.type, data.typeId, {
          automationId: '',
        });
      }

      toast({
        title: t('success'),
        description: 'Automation deleted successfully',
        status: 'success',
        duration: 2000,
      });

      navigate(-1);
      alertDelete.onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [data, navigate, toast, alertDelete, t]);

  // ==================== HELPER FUNCTIONS ====================
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Not set';
    return moment(timestamp * 1000).format('MMMM DD, YYYY - HH:mm');
  }, []);

  const getStatusConfig = useCallback((isActive) => {
    return isActive
      ? { icon: FiCheckCircle, color: 'green', label: 'Active' }
      : { icon: FiXCircle, color: 'red', label: 'Inactive' };
  }, []);

  // ==================== LOADING STATE ====================
  if (isLoading) {
    return (
      <Stack p={[4, 6, 8]} spacing={6}>
        <HStack>
          <BackButtons />
          <Spacer />
          <Skeleton height="40px" width="100px" />
          <Skeleton height="40px" width="100px" />
        </HStack>
        <Skeleton height="200px" borderRadius="xl" />
        <Grid templateColumns={['1fr', '1fr', 'repeat(3, 1fr)']} gap={6}>
          <Skeleton height="300px" borderRadius="xl" />
          <Skeleton height="300px" borderRadius="xl" />
          <Skeleton height="300px" borderRadius="xl" />
        </Grid>
      </Stack>
    );
  }

  if (!data) {
    return (
      <Stack p={[4, 6, 8]} spacing={6}>
        <HStack>
          <BackButtons />
        </HStack>
        <Box
          textAlign="center"
          py={10}
          px={6}
          bg="white"
          borderRadius="xl"
          shadow="sm"
        >
          <Icon as={FiAlertCircle} boxSize={12} color="gray.400" mb={4} />
          <Heading size="md" color="gray.600" mb={2}>
            {t('notFound')}
          </Heading>
          <Text color="gray.500">
            The automation you're looking for doesn't exist.
          </Text>
        </Box>
      </Stack>
    );
  }

  const statusConfig = getStatusConfig(data.isActive);

  // ==================== RENDER ====================
  return (
    <Stack p={[4, 6, 8]} spacing={6} bg="gray.50" minH="100vh">
      {/* Header */}
      <Flex
        bg="white"
        p={4}
        borderRadius="xl"
        shadow="sm"
        align="center"
        gap={4}
        flexWrap="wrap"
      >
        <BackButtons />
        <Spacer />
        <DynamicButton
          action="update"
          title={t('edit')}
          variant="solid"
          onClick={() => navigate(`/automation/edit/${data.id}`)}
        />
        <DynamicButton
          action="delete"
          title={t('delete')}
          variant="solid"
          onClick={alertDelete.onOpen}
        />
      </Flex>

      {/* Main Info Card */}
      <SectionCard
        title={t('Automation')}
        icon={FiRepeat}
        colorScheme="purple"
      >
        <Flex
          direction={['column', 'column', 'row']}
          justify="space-between"
          align={['flex-start', 'flex-start', 'center']}
          gap={4}
        >
          <VStack align="flex-start" spacing={2} flex={1}>
            <Heading size="lg" color="gray.800">
              {data.dataAutomation?.title || 'Untitled Automation'}
            </Heading>
            <HStack spacing={2}>
              <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                {data.type}
              </Badge>
              <Badge colorScheme="gray" fontSize="sm" px={3} py={1}>
                ID: {data.typeId}
              </Badge>
            </HStack>
          </VStack>

          <VStack align={['flex-start', 'flex-start', 'flex-end']} spacing={2}>
            <HStack spacing={2}>
              <Icon as={statusConfig.icon} color={`${statusConfig.color}.500`} />
              <Tag
                size="lg"
                colorScheme={statusConfig.color}
                fontWeight="bold"
              >
                {statusConfig.label}
              </Tag>
            </HStack>
            <HStack spacing={2} color="gray.500" fontSize="sm">
              <Icon as={FiClock} />
              <Text>
                {data.timeActionSchedule
                  ? formatDate(data.timeActionSchedule)
                  : 'Runs immediately'}
              </Text>
            </HStack>
          </VStack>
        </Flex>

        <Divider />

        {/* Quick Info Grid */}
        <Grid templateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)']} gap={3}>
          <InfoCard
            icon={FiCalendar}
            label={t('porject')}
            value={data.projectId}
            colorScheme="blue"
          />
          <InfoCard
            icon={FiClock}
            label={t('lastActivity')}
            value={
              data.timeActionSchedule
                ? formatDate(data.timeActionSchedule)
                : 'Not run yet'
            }
            colorScheme="orange"
          />
          <InfoCard
            icon={FiCalendar}
            label={t('expired')}
            value={formatDate(data.untilAction)}
            colorScheme="red"
          />
        </Grid>
      </SectionCard>

      {/* Details Grid */}
      <Grid templateColumns={['1fr', '1fr', 'repeat(3, 1fr)']} gap={6}>
        {/* Source Data */}
        <SectionCard
          title={t('dataFrom')}
          icon={FiFile}
          colorScheme="green"
        >
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('title')}
              </Text>
              <Text fontWeight="semibold" color="gray.800">
                {data.dataAutomation?.title || '-'}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('column')}
              </Text>
              <Badge colorScheme="purple">
                {data.dataAutomation?.column || '-'}
              </Badge>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('score')}
              </Text>
              <Text fontWeight="semibold" color="gray.800">
                {data.dataAutomation?.score || '0'}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('dueOn')}
              </Text>
              <HStack>
                <Icon as={FiCalendar} color="gray.400" boxSize={4} />
                <Text fontSize="sm" color="gray.700">
                  {formatDate(data.dataAutomation?.dueOn)}
                </Text>
              </HStack>
            </Box>
          </VStack>
        </SectionCard>

        {/* Action Details */}
        <SectionCard
          title={t('action')}
          icon={FiArrowRight}
          colorScheme="orange"
        >
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('action')}
              </Text>
              <Badge colorScheme="orange" fontSize="md" p={2}>
                {data.action || '-'}
              </Badge>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('time')}
              </Text>
              <HStack>
                <Icon as={FiClock} color="gray.400" boxSize={4} />
                <Text fontWeight="semibold" color="gray.800">
                  {data.timeAction || '-'}
                </Text>
              </HStack>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t('day')}
              </Text>
              {data.dayAction && data.dayAction.length > 0 ? (
                <Flex gap={2} flexWrap="wrap">
                  {data.dayAction.map((day, index) => (
                    <Badge
                      key={index}
                      colorScheme="blue"
                      textTransform="capitalize"
                      px={2}
                      py={1}
                    >
                      {day}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  All days
                </Text>
              )}
            </Box>
          </VStack>
        </SectionCard>

        {/* Destination */}
        <SectionCard
          title={t('to2')}
          icon={FiFile}
          colorScheme="teal"
        >
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t('file')}
              </Text>
              <Badge colorScheme="teal" fontSize="md" p={2}>
                {data.fileTo || '-'}
              </Badge>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t('file')} data
              </Text>
              {data.fileToId && data.fileToId.length > 0 ? (
                <VStack align="stretch" spacing={2}>
                  {data.fileToId.map((file, index) => (
                    <Box
                      key={index}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.200"
                    >
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                        {file.title || 'Untitled'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        ID: {file.id}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No files selected
                </Text>
              )}
            </Box>
          </VStack>
        </SectionCard>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <AlertDialogV2
        isOpen={alertDelete.isOpen}
        onClose={alertDelete.onClose}
        title={`${t('delete')} ${t('Automation')} ${data.dataAutomation?.title || ''}`}
        onAction={handleDelete}
      />
    </Stack>
  );
}

export default AutomationDetailPage;